import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import VpFieldMappingInputs from '@vp-ui/form-fields/VpFieldMappingInputs/VpFieldMappingInputs';

/**
 * Shown when adding a video to a library/plugin that has no field mapping configured yet.
 * Collects the mapping and calls onConfirm({ titleElementId, descriptionElementId }).
 *
 * elements: [{ id, label }] — available DATA-type campaign elements for the picker
 * targetName: string — name of the library or plugin being added to
 * open: boolean
 * onConfirm: ({ titleElementId, descriptionElementId }) => Promise<void>
 * onCancel: () => void
 */
const VpFieldMappingDialog = ({ open, onCancel, onConfirm, elements = [], targetName = '' }) => {
  const [titleElementId, setTitleElementId] = useState(null);
  const [descriptionElementId, setDescriptionElementId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = ({ titleElementId: t, descriptionElementId: d }) => {
    setTitleElementId(t);
    setDescriptionElementId(d);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm({ titleElementId, descriptionElementId });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Configure Video Labels</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose which submission fields become the title and description for videos in{' '}
          <strong>{targetName}</strong>. You can change this later in settings.
        </Typography>
        <VpFieldMappingInputs
          elements={elements}
          titleElementId={titleElementId}
          descriptionElementId={descriptionElementId}
          onChange={handleChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Skip &amp; Add
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={16} /> : 'Save & Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VpFieldMappingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  elements: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })),
  targetName: PropTypes.string
};

export default VpFieldMappingDialog;



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpFieldMappingDialog/VpFieldMappingDialog.jsx